import React, { useState, useCallback, useMemo } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  CheckCircle,
  FileText,
  FolderPlus,
  LayoutDashboard,
  Calendar,
  ShieldCheck,
  BookOpen,
  FileCode,
  BarChart,
  FileBarChart2,
  Bell,
  HelpCircle,
  PenSquare,
  PanelLeft,
  MessageSquare,
} from "lucide-react";
import { Separator } from "@/components/ui/separator";
import LicitarsLogo from "../LicitarsLogo";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";

const AppSidebar = React.memo(() => {
  const location = useLocation();
  const currentPath = location.pathname;
  const [collapsed, setCollapsed] = useState(false);

  // Memoize the isActive function
  const isActive = useCallback(
    (path: string) =>
      currentPath === path || currentPath.startsWith(`${path}/`),
    [currentPath],
  );

  // Memoize the getNavLinkClass function
  const getNavLinkClass = useCallback(
    ({ isActive }: { isActive: boolean }) =>
      isActive ? "nav-link active" : "nav-link",
    [],
  );

  // Memoize initial states
  const [processesOpen, setProcessesOpen] = useState(
    useMemo(
      () => isActive("/processes") || isActive("/processes/new"),
      [isActive],
    ),
  );

  const [documentsOpen, setDocumentsOpen] = useState(
    useMemo(
      () =>
        isActive("/documents") ||
        isActive("/documents/create") ||
        isActive("/editor"),
      [isActive],
    ),
  );

  const [resourcesOpen, setResourcesOpen] = useState(
    useMemo(() => isActive("/library") || isActive("/templates"), [isActive]),
  );

  const [analyticsOpen, setAnalyticsOpen] = useState(
    useMemo(() => isActive("/metrics") || isActive("/reports"), [isActive]),
  );

  // Memoize toggle functions
  const toggleSidebar = useCallback(() => {
    setCollapsed((prev) => !prev);
  }, []);

  const toggleProcesses = useCallback(() => {
    setProcessesOpen((prev) => !prev);
  }, []);

  const toggleDocuments = useCallback(() => {
    setDocumentsOpen((prev) => !prev);
  }, []);

  const toggleResources = useCallback(() => {
    setResourcesOpen((prev) => !prev);
  }, []);

  const toggleAnalytics = useCallback(() => {
    setAnalyticsOpen((prev) => !prev);
  }, []);

  // Memoize the sidebar content
  const sidebarContent = useMemo(
    () => (
      <div className={`sidebar ${collapsed ? "collapsed" : ""}`}>
        <div className="sidebar-header flex items-center justify-center py-6">
          <LicitarsLogo size={collapsed ? "small" : "medium"} />
        </div>

        <div className="sidebar-toggle-btn mb-2 self-end mr-2">
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 sidebar-toggle"
            onClick={toggleSidebar}
            id="sidebar-toggle-btn"
          >
            <PanelLeft />
            <span className="sr-only">Toggle Sidebar</span>
          </Button>
        </div>
        <Separator className="my-1 sidebar-separator" />

        <div className="sidebar-content flex flex-col min-h-0 flex-1 gap-2 overflow-auto">
          <div
            className="sidebar-section sidebar-section-menu relative flex w-full min-w-0 flex-col p-2"
            id="sidebar-section-menu"
          >
            <div className="sidebar-section-title flex items-center gap-2">
              {!collapsed && <span>Menu</span>}
            </div>
            <div className="w-full text-sm">
              <ul className="sidebar-list flex w-full min-w-0 flex-col gap-1">
                <li className="sidebar-list-item group/menu-item relative">
                  <NavLink
                    to="/"
                    className={getNavLinkClass}
                    id="sidebar-link-dashboard"
                  >
                    <LayoutDashboard size={20} />
                    {!collapsed && <span>Visão Geral</span>}
                  </NavLink>
                </li>
              </ul>
            </div>
          </div>

          <div
            className="sidebar-section sidebar-section-processos relative flex w-full min-w-0 flex-col p-2"
            id="sidebar-section-processos"
          >
            <div
              className={`sidebar-section-title flex items-center gap-2 ${collapsed ? "justify-center" : ""}`}
            >
              {!collapsed && <span>Processos</span>}
            </div>
            <div className="w-full text-sm">
              <ul className="sidebar-list flex w-full min-w-0 flex-col gap-1">
                <li className="sidebar-list-item group/menu-item relative">
                  <NavLink
                    to="/processes"
                    className={getNavLinkClass}
                    id="sidebar-link-processes"
                  >
                    <CheckCircle size={20} />
                    {!collapsed && <span>Meus Processos</span>}
                  </NavLink>
                </li>

                <li className="sidebar-list-item group/menu-item relative">
                  <NavLink
                    to="/processes/new"
                    className={getNavLinkClass}
                    id="sidebar-link-processes-new"
                  >
                    <FolderPlus size={20} />
                    {!collapsed && <span>Novo Processo</span>}
                  </NavLink>
                </li>
              </ul>
            </div>
          </div>

          <div
            className="sidebar-section sidebar-section-documentos relative flex w-full min-w-0 flex-col p-2"
            id="sidebar-section-documentos"
          >
            <div
              className={`sidebar-section-title flex items-center gap-2 ${collapsed ? "justify-center" : ""}`}
            >
              {!collapsed && <span>Documentos</span>}
            </div>
            <div className="w-full text-sm">
              <ul className="sidebar-list flex w-full min-w-0 flex-col gap-1">
                <li className="sidebar-list-item group/menu-item relative">
                  <NavLink
                    to="/documents"
                    className={getNavLinkClass}
                    id="sidebar-link-documents"
                  >
                    <FileText size={20} />
                    {!collapsed && <span>Meus Documentos</span>}
                  </NavLink>
                </li>

                <li className="sidebar-list-item group/menu-item relative">
                  <NavLink
                    to="/documents/create"
                    className={getNavLinkClass}
                    id="sidebar-link-documents-create"
                  >
                    <FileCode size={20} />
                    {!collapsed && <span>Gerar Documento</span>}
                  </NavLink>
                </li>

                <li className="sidebar-list-item group/menu-item relative">
                  <NavLink
                    to="/editor"
                    className={getNavLinkClass}
                    id="sidebar-link-editor"
                  >
                    <PenSquare size={20} />
                    {!collapsed && <span>Editor</span>}
                  </NavLink>
                </li>
              </ul>
            </div>
          </div>

          <div
            className="sidebar-section sidebar-section-recursos relative flex w-full min-w-0 flex-col p-2"
            id="sidebar-section-recursos"
          >
            <div
              className={`sidebar-section-title flex items-center gap-2 ${collapsed ? "justify-center" : ""}`}
            >
              {!collapsed && <span>Recursos</span>}
            </div>
            <div className="w-full text-sm">
              <ul className="sidebar-list flex w-full min-w-0 flex-col gap-1">
                <li className="sidebar-list-item group/menu-item relative">
                  <NavLink
                    to="/library"
                    className={getNavLinkClass}
                    id="sidebar-link-library"
                  >
                    <BookOpen size={20} />
                    {!collapsed && <span>Biblioteca Legal</span>}
                  </NavLink>
                </li>

                <li className="sidebar-list-item group/menu-item relative">
                  <NavLink
                    to="/templates"
                    className={getNavLinkClass}
                    id="sidebar-link-templates"
                  >
                    <FileText size={20} />
                    {!collapsed && <span>Modelos</span>}
                  </NavLink>
                </li>

                <li className="sidebar-list-item group/menu-item relative">
                  <Link
                    to="/chat8"
                    className={cn(
                      "flex items-center gap-2 rounded-lg px-3 py-2 text-gray-200 transition-colors hover:bg-gray-800 hover:text-white",
                      currentPath === "/chat8" && "bg-gray-800 text-white"
                    )}
                    id="sidebar-link-chat8"
                  >
                    <MessageSquare className="h-4 w-4" />
                    {!collapsed && <span>Chat8</span>}
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          <div
            className="sidebar-section sidebar-section-analises relative flex w-full min-w-0 flex-col p-2"
            id="sidebar-section-analises"
          >
            <div
              className={`sidebar-section-title flex items-center gap-2 ${collapsed ? "justify-center" : ""}`}
            >
              {!collapsed && <span>Análises</span>}
            </div>
            <div className="w-full text-sm">
              <ul className="sidebar-list flex w-full min-w-0 flex-col gap-1">
                <li className="sidebar-list-item group/menu-item relative">
                  <NavLink
                    to="/metrics"
                    className={getNavLinkClass}
                    id="sidebar-link-metrics"
                  >
                    <BarChart size={20} />
                    {!collapsed && <span>Métricas</span>}
                  </NavLink>
                </li>

                <li className="sidebar-list-item group/menu-item relative">
                  <NavLink
                    to="/reports"
                    className={getNavLinkClass}
                    id="sidebar-link-reports"
                  >
                    <FileBarChart2 size={20} />
                    {!collapsed && <span>Relatórios</span>}
                  </NavLink>
                </li>
              </ul>
            </div>
          </div>

          <Separator className="my-2 sidebar-separator" />

          <div
            className="sidebar-section sidebar-section-notificacoes relative flex w-full min-w-0 flex-col p-2"
            id="sidebar-section-notificacoes"
          >
            <div className="sidebar-section-title flex items-center gap-2">
              {!collapsed && <span>Notificações</span>}
            </div>
            <div className="w-full text-sm">
              <ul className="sidebar-list flex w-full min-w-0 flex-col gap-1">
                <li className="sidebar-list-item group/menu-item relative">
                  <NavLink
                    to="/notifications"
                    className={getNavLinkClass}
                    id="sidebar-link-notifications"
                  >
                    <Bell size={20} />
                    {!collapsed && <span>Notificações</span>}
                  </NavLink>
                </li>

                <li className="sidebar-list-item group/menu-item relative">
                  <NavLink
                    to="/help"
                    className={getNavLinkClass}
                    id="sidebar-link-help"
                  >
                    <HelpCircle size={20} />
                    {!collapsed && <span>Ajuda e Suporte</span>}
                  </NavLink>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    ),
    [collapsed, getNavLinkClass, isActive, toggleSidebar],
  );

  return sidebarContent;
});

AppSidebar.displayName = "AppSidebar";

export default AppSidebar;
